import ListeningLogo from '@listening/assets/astro/icons/new-design-25/compare/listening-logo.svg?react';
import { cn } from '@listening/utils/react-helpers';
import { Check, Minus, Xmark } from 'iconoir-react';
import { Star } from 'iconoir-react/solid';
import { Fragment } from 'react';
import { CtaButton } from '@/components/astro/cta-button/cta-button';
import * as m from '@/paraglide/messages';

type Support = 'yes' | 'some' | 'no';

type Row = {
  key: string;
  label: () => string;
  others: Support;
  note?: () => string;
  noteDesktopOnly?: boolean;
};

const GROUPS: ReadonlyArray<{
  key: string;
  title: () => string;
  rows: ReadonlyArray<Row>;
}> = [
  {
    key: 'how',
    title: m.why_listening_compare_group_how,
    rows: [
      {
        key: 'voices',
        label: m.why_listening_compare_natural_voices,
        others: 'some',
      },
      {
        key: 'clutter',
        label: m.why_listening_compare_skips_clutter,
        others: 'no',
        note: m.why_listening_compare_note_reads_aloud,
      },
      {
        key: 'layouts',
        label: m.why_listening_compare_column_layouts,
        others: 'no',
      },
      {
        key: 'pronunciation',
        label: m.why_listening_compare_pronunciation,
        others: 'some',
      },
    ],
  },
  {
    key: 'sources',
    title: m.why_listening_compare_group_sources,
    rows: [
      {
        key: 'documents',
        label: m.why_listening_compare_documents,
        others: 'some',
      },
      {
        key: 'newsletters',
        label: m.why_listening_compare_newsletters,
        others: 'some',
      },
      {
        key: 'integrations',
        label: m.why_listening_compare_integrations,
        others: 'some',
      },
      {
        key: 'overviews',
        label: m.why_listening_compare_overviews,
        others: 'some',
      },
    ],
  },
  {
    key: 'plan',
    title: m.plan_your_plan,
    rows: [
      {
        key: 'unlimited',
        label: m.why_listening_compare_unlimited,
        others: 'no',
        note: m.why_listening_compare_note_caps,
        noteDesktopOnly: true,
      },
      {
        key: 'devices',
        label: m.why_listening_compare_all_apps,
        others: 'some',
        note: m.why_listening_compare_note_subscriptions,
        noteDesktopOnly: true,
      },
    ],
  },
];

const COLUMN =
  'bg-azure-595/[0.07] shadow-[inset_1px_0_0_rgb(5_135_251/0.4),inset_-1px_0_0_rgb(5_135_251/0.4)]';

const SUPPORT_LABEL = {
  yes: m.plan_included,
  some: m.why_listening_compare_some_apps,
  no: m.why_listening_compare_not_included,
} satisfies Record<Support, () => string>;

function Mark({ support }: { support: Support }) {
  return (
    <span className='flex items-center gap-2 text-xs text-white/70 sm:justify-center'>
      {support === 'yes' && (
        <span className='grid size-6 shrink-0 place-items-center rounded-full bg-azure-595 shadow-[0_0_20px_rgb(5_135_251/0.5)] sm:size-7'>
          <Check
            aria-hidden='true'
            className='size-3.5 text-white sm:size-4'
            strokeWidth={2.6}
          />
        </span>
      )}
      {support === 'some' && (
        <span className='grid size-6 shrink-0 place-items-center rounded-full bg-white/10 sm:size-7'>
          <Minus
            aria-hidden='true'
            className='size-3.5 text-white/60 sm:size-4'
            strokeWidth={2.2}
          />
        </span>
      )}
      {support === 'no' && (
        <span className='grid size-6 shrink-0 place-items-center rounded-full ring-1 ring-white/12 sm:size-7'>
          <Xmark
            aria-hidden='true'
            className='size-3 text-white/35 sm:size-3.5'
            strokeWidth={2}
          />
        </span>
      )}
      <span className='sm:sr-only'>{SUPPORT_LABEL[support]()}</span>
    </span>
  );
}

function Legend() {
  return (
    <ul className='hidden items-center gap-4 text-xs text-white/45 sm:flex'>
      <li className='flex items-center gap-1.5'>
        <span className='size-2 rounded-full bg-azure-595' />
        {m.plan_included()}
      </li>
      <li className='flex items-center gap-1.5'>
        <Minus className='size-3 text-white/60' strokeWidth={2.4} />
        {m.why_listening_compare_some_apps()}
      </li>
      <li className='flex items-center gap-1.5'>
        <Xmark className='size-3 text-white/40' strokeWidth={2.4} />
        {m.why_listening_compare_not_included()}
      </li>
    </ul>
  );
}

function GroupHeader({ title }: { title: string }) {
  return (
    <tr className='block sm:table-row'>
      <th
        className='block pt-7 pb-2 text-left font-mono text-[11px] font-medium tracking-[0.12em] text-white/40 uppercase sm:table-cell'
        scope='colgroup'
      >
        {title}
      </th>
      <td aria-hidden='true' className={cn('hidden sm:table-cell', COLUMN)} />
      <td aria-hidden='true' className='hidden sm:table-cell' />
    </tr>
  );
}

function FeatureRow({ row }: { row: Row }) {
  return (
    <tr className='grid grid-cols-2 gap-x-3 border-t border-white/[0.07] py-4 sm:table-row sm:border-0 sm:py-0'>
      <th
        className='col-span-2 pb-3 text-left text-base leading-snug font-medium text-white/90 sm:h-16 sm:border-t sm:border-white/[0.07] sm:py-2 sm:pr-3'
        id={`why-compare-${row.key}`}
        scope='row'
      >
        {row.label()}
      </th>
      <td
        className={cn(
          'min-w-0 rounded-xl p-3 sm:rounded-none sm:border-t sm:border-white/[0.07] sm:px-0 sm:py-2',
          COLUMN,
        )}
        headers={`why-compare-${row.key} why-compare-listening`}
      >
        <div className='flex flex-col gap-2 sm:items-center sm:gap-0'>
          <span
            aria-hidden='true'
            className='text-xs font-semibold text-white sm:hidden'
          >
            {m.common_app_name()}
          </span>
          <Mark support='yes' />
        </div>
      </td>
      <td
        className='min-w-0 rounded-xl bg-white/[0.03] p-3 sm:rounded-none sm:border-t sm:border-white/[0.07] sm:bg-transparent sm:px-2 sm:py-2'
        headers={`why-compare-${row.key} why-compare-others`}
      >
        <div className='flex flex-col gap-2 sm:items-center sm:gap-1'>
          <span
            aria-hidden='true'
            className='text-xs font-medium text-white/60 sm:hidden'
          >
            {m.why_listening_compare_others()}
          </span>
          <Mark support={row.others} />
          {row.note && (
            <span
              className={cn(
                'text-xs leading-relaxed text-white/50 sm:text-center sm:text-[11px] sm:leading-tight sm:text-white/40',
                row.noteDesktopOnly && 'hidden sm:block',
              )}
            >
              {row.note()}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

export function ComparisonSection() {
  return (
    <section
      aria-labelledby='why-compare-heading'
      className='relative isolate scroll-mt-20 overflow-hidden bg-(--v2-text-primary) px-5 py-16 sm:px-6 md:py-28'
      id='why-compare'
    >
      <div
        aria-hidden='true'
        className='absolute inset-x-0 top-1/4 -z-10 mx-auto h-[640px] max-w-3xl rounded-full bg-azure-595/20 blur-[150px]'
      />
      <div
        aria-hidden='true'
        className='absolute inset-0 -z-10 bg-[radial-gradient(rgb(255_255_255/0.06)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] bg-size-[22px_22px]'
      />

      <div className='mx-auto max-w-2xl text-center'>
        <p className='mb-4 font-mono text-[11px] font-medium tracking-[0.14em] text-azure-105 uppercase sm:text-xs'>
          {m.why_listening_compare_eyebrow()}
        </p>
        <h2
          className='text-[32px] leading-[1.05] font-semibold tracking-[-0.04em] text-balance text-white sm:text-5xl'
          id='why-compare-heading'
        >
          {m.why_listening_compare_title()}
        </h2>
        <p className='mt-4 text-base leading-relaxed text-pretty text-white/55 sm:mt-5 sm:text-lg'>
          {m.why_listening_compare_description()}
        </p>
      </div>

      <div className='mx-auto mt-10 max-w-3xl rounded-[28px] bg-white/[0.03] p-1.5 ring-1 ring-white/[0.08] sm:mt-14 sm:p-2'>
        <div className='rounded-[22px] bg-(--v2-text-primary)/70 px-3 pb-2 sm:px-8 sm:pb-8'>
          <table className='block w-full table-fixed border-separate border-spacing-0 text-white sm:table'>
            <caption className='sr-only'>
              {m.why_listening_compare_caption()}
            </caption>
            <thead className='sr-only sm:not-sr-only sm:table-header-group'>
              <tr>
                <th className='w-[46%] sm:w-[48%]' scope='col'>
                  <span className='sr-only'>{m.nav_features()}</span>
                </th>
                <th
                  className={cn(
                    'rounded-t-2xl px-2 pt-6 pb-3 sm:pt-7',
                    COLUMN,
                    'shadow-[inset_1px_0_0_rgb(5_135_251/0.4),inset_-1px_0_0_rgb(5_135_251/0.4),inset_0_1px_0_rgb(5_135_251/0.4)]',
                  )}
                  id='why-compare-listening'
                  scope='col'
                >
                  <span className='inline-flex flex-col items-center gap-1.5 text-sm font-semibold sm:flex-row sm:gap-2 sm:text-base'>
                    <ListeningLogo
                      aria-hidden='true'
                      className='h-4 w-auto text-azure-595 sm:h-[18px]'
                    />
                    {m.common_app_name()}
                  </span>
                </th>
                <th
                  className='px-2 pt-6 pb-3 text-xs leading-snug font-medium text-balance text-white/45 sm:pt-7 sm:text-sm'
                  id='why-compare-others'
                  scope='col'
                >
                  {m.why_listening_compare_others()}
                </th>
              </tr>
            </thead>
            <tbody className='block sm:table-row-group'>
              {GROUPS.map((group) => (
                <Fragment key={group.key}>
                  <GroupHeader title={group.title()} />
                  {group.rows.map((row) => (
                    <FeatureRow key={row.key} row={row} />
                  ))}
                </Fragment>
              ))}
              <tr className='hidden sm:table-row'>
                <td className='pt-4 align-bottom'>
                  <Legend />
                </td>
                <td
                  className={cn(
                    'rounded-b-2xl px-2 pt-4 pb-4 sm:px-3 sm:pb-5',
                    COLUMN,
                    'shadow-[inset_1px_0_0_rgb(5_135_251/0.4),inset_-1px_0_0_rgb(5_135_251/0.4),inset_0_-1px_0_rgb(5_135_251/0.4)]',
                  )}
                >
                  <CtaButton
                    className='hidden h-10 w-full px-3 py-0 text-sm font-semibold hover:bg-primary/90 sm:flex'
                    label={m.common_try_for_free()}
                    variant='default'
                  />
                </td>
                <td aria-hidden='true' />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className='mx-auto mt-8 flex max-w-3xl flex-col items-center gap-5 text-center sm:mt-10'>
        <CtaButton
          className='h-13 w-full max-w-xs px-8 py-0 text-base font-semibold shadow-[0_8px_30px_rgb(5_135_251/0.35)] hover:bg-primary/90 sm:hidden'
          label={m.common_try_for_free()}
          variant='default'
        />
        <p className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-white/55'>
          <span className='inline-flex items-center gap-1.5'>
            <Star aria-hidden='true' className='size-3.5 text-gold-375' />
            <span className='font-semibold text-white/85'>4.4</span>
            {m.why_listening_hero_rating_short()}
          </span>
          <span aria-hidden='true' className='hidden text-white/20 sm:inline'>
            /
          </span>
          <span>{m.why_listening_compare_platforms()}</span>
        </p>
      </div>
    </section>
  );
}
